/**
 * RESPONSIBILITY: carry the site's shared wording to the views that print it.
 *
 * The interface says the same handful of words on every page — the trail back to the
 * top, the count over a filtered list, the names of the pages. Threading one section
 * through every component that shows one of them would put a `words` prop on parts of
 * the tree that do nothing with it, so it is read once at the composition root and
 * offered from there.
 *
 * This is NOT a way round "presentation components take content as props". Nothing here
 * fetches, decides a permission or saves; it is the same content arriving by a shorter
 * route, and the one place it is loaded is still the page that composes the site.
 *
 * An absent provider yields an empty object rather than throwing: a view rendered in
 * isolation should come out with no words, never with a crash.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { WordsSection } from '@app/sections';

/**
 * Opening the editor for these words, offered the same way they are.
 *
 * They are printed in the breadcrumb bar on every page below the front one, and a
 * visible thing with no way to change it is the one thing an editor cannot forgive. The
 * bar has no section of its own to hang a pencil on, so the handle travels with the
 * content it belongs to.
 */
export interface WordsEdit { canEdit: boolean; open: () => void }

const NONE = {} as WordsSection;
const NO_EDIT: WordsEdit = { canEdit: false, open: () => {} };

const Ctx = createContext<WordsSection>(NONE);
const EditCtx = createContext<WordsEdit>(NO_EDIT);

export function WordsProvider(
  { value, edit, children }:
  { value: WordsSection | null; edit?: WordsEdit; children: ReactNode },
) {
  return (
    <Ctx.Provider value={value ?? NONE}>
      <EditCtx.Provider value={edit ?? NO_EDIT}>{children}</EditCtx.Provider>
    </Ctx.Provider>
  );
}

export const useWords = (): WordsSection => useContext(Ctx);
export const useWordsEdit = (): WordsEdit => useContext(EditCtx);
